/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChange,
  SimpleChanges,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { SubSink } from 'subsink';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { SelectItem, SortEvent } from 'primeng/api';
import { BehaviorSubject } from 'rxjs';
import { OverlayPanel } from 'primeng/overlaypanel';
import moment from 'moment';
import {
  ModifiedSessionDocumentInfo,
  SessionDocumentInfo,
} from '../../models/session/session-document-info.model';
import { SessionDocumentDiscountState } from '../../models/session/session-document-discount-state.model';
import {
  overPaymentValidator,
  payableAmountGreaterThanOpenBalanceValidator,
  shortPaymentValidator,
} from '../../directives/payableamount-input-directive';
import { ModifiedDocumentMeta } from '../../models/session/modified-document-meta.model';
import { PaymentRulesResponseV1 } from '../../models/session/payment-rule-response-v1.model';
import {
  AppliedDiscountMeta,
  DiscountExclusionReason,
  DisputeReason,
  PayablePaymentStatus,
  PaymentPreviewPayableV1Response,
} from '../../models';
import { requiredNoWhitespace } from '../../validators';
import { sortDocuments } from '../../helpers/document-helper';
import { PayOtherAmountReasonCodes } from '../../models/pay-other-amount/pay-other-amount-reason-codes.model';
import { Table } from 'primeng/table';
import { ButtonComponent } from '@arc/aureus';
import { PayOtherShortPaidReasonService } from '../../services/payother-shortpaid-reason.service';
import { BreakpointObserver } from '@angular/cdk/layout';

interface SharedDocumentTableChanges extends SimpleChanges {
  documents: SimpleChange;
  modifiedDocuments: SimpleChange;
  isReadonly: SimpleChange;
  excludedColumns: SimpleChange;
  paymentRules: SimpleChange;
  disputeReasons: SimpleChange;
  paymentDate: SimpleChange;
}

type ColumnField =
  | 'payableRestrictions'
  | 'invoiceNumber'
  | 'confirmationNumber'
  | 'transactionNumber'
  | 'buyerAccount'
  | 'poNumber'
  | 'documentDate'
  | 'dueDate'
  | 'discountDate'
  | 'discountAmount'
  | 'originalBalance'
  | 'openBalance'
  | 'disputedAmount'
  | 'amountPaid'
  | 'payableAmount'
  | 'editPayableAmount';

interface Column {
  field: ColumnField;
  header: string;
  visible: boolean;
  sortable: boolean;
  wrap?: boolean;
  currency?: boolean;
  frozen?: boolean;
  alignFrozen?: 'right';
  width?: string;
}

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'shared-document-table-component',
  templateUrl: './shared-document-table.component.html',
  styleUrls: ['./shared-document-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SharedDocumentTableComponent
  implements OnChanges, OnInit, OnDestroy, AfterViewChecked
{
  @ViewChild('editPaymentOverlayPanel', { static: false })
  editPaymentOverlayPanel: OverlayPanel;

  @ViewChild('documentTable', { static: false })
  documentTable: Table;

  @ViewChildren('button', { read: ButtonComponent })
  pencils: QueryList<ButtonComponent>;

  @Output()
  editPayableAmount = new EventEmitter<SessionDocumentInfo>();

  @Output() markDocumentAsModified = new EventEmitter<ModifiedDocumentMeta>();
  @Output() updateAppliedDiscountAmount = new EventEmitter();
  @Output() selectedDocumentsChanged = new EventEmitter<
    ModifiedSessionDocumentInfo[]
  >();

  @Input() isReadOnly: boolean;
  @Input() excludedColumns: ColumnField[];
  @Input() modifiedDocuments: ModifiedSessionDocumentInfo[];
  @Input() paymentRules: PaymentRulesResponseV1;
  @Input() statementPaymentAllowed: boolean;
  @Input() disputeReasons: DisputeReason[];
  @Input() paymentDate: Date;
  @Input() hasMultipleTransactions: boolean;
  @Input() discountMetadata: AppliedDiscountMeta[];
  @Input() previewPayables: PaymentPreviewPayableV1Response[];

  marginStyle = {};
  freezeColumns = true;
  private isRendered = false;

  formGroup: UntypedFormGroup;
  columnMap: Record<ColumnField, Column>;
  columns: Column[];

  modifiedSessionDocuments: ModifiedSessionDocumentInfo[] = [];
  selectedDocuments: ModifiedSessionDocumentInfo[] = [];

  private editableRowData: ModifiedSessionDocumentInfo;
  public currentRowData = {} as ModifiedSessionDocumentInfo;

  public _isReadOnly$ = new BehaviorSubject(false);
  public selectedPayOtherShortPaidReason;

  private editPaymentCurrencyInput: AbstractControl<number>;

  private subSink = new SubSink();

  private shortPayRowIndex = 1;

  scrollHeight = '175px';
  windowHeight: number;
  currencyInputLabel: string;
  displayDialogBox: boolean;
  currencyOptions: SelectItem[] = [];
  noValidate = false;
  formControlName = '';
  errorMessages: { [key: string]: string };
  disputeReasonItems: SelectItem<DisputeReason>[];
  filteredDisputeReasonItems: SelectItem<DisputeReason>[];
  editPaymentReasonDropdownFormControl: AbstractControl;
  currencyInputFormControl: AbstractControl<{
    amount: number;
    currency: string;
  }>;

  enableAutocompleteDropdownMode: boolean;
  paymentDateChange: boolean;
  reasonDropdownPlaceHolderKey = 'ARC_PAYMENT_SHORT_PAY_SELECT_OR_ENTER_REASON';
  resizeTimer: number;

  public documentDiscountState: SessionDocumentDiscountState;
  public isDiscountApplied: boolean;

  constructor(
    private translationService: TranslateService,
    private cdref: ChangeDetectorRef,
    private fb: UntypedFormBuilder,
    private el: ElementRef,
    private shortPaidStatusService: PayOtherShortPaidReasonService,
    private breakpointObserver: BreakpointObserver,
  ) {
    this.initializeColumns();
    this.createEditPayableAmountForm();
  }

  ngOnChanges(changes: SharedDocumentTableChanges): void {
    if (changes.modifiedDocuments) {
      this.onDocumentsChanged(
        changes.modifiedDocuments.currentValue as ModifiedSessionDocumentInfo[],
      );
    }

    if (changes.isReadOnly) {
      this.isTableReadOnly = this.isReadOnly;
      this.onDocumentsChanged(this.modifiedSessionDocuments);
      if (this.isTableReadOnly) {
        this.closeOverlayPanel();
      }
    }

    if (changes.paymentRules || changes.excludedColumns) {
      this.onDocumentsChanged(this.modifiedSessionDocuments);
    }

    if (changes.disputeReasons) {
      this.loadDisputeReasons();
    }
    this.paymentDateChange = !!changes.paymentDate;
    if (this.paymentDateChange) {
      const isStrictValidDate = moment(
        changes.paymentDate.currentValue as Date,
        'MM-DD-YYYY',
        true,
      ).isValid();
      if (isStrictValidDate) {
        // emit call to update payment preview
        this.updateAppliedDiscountAmount.emit();
      }
    }
  }

  ngOnInit(): void {
    this.shortPayRowIndex = -1; // No ShortPay

    if (this.modifiedSessionDocuments?.length > 3) {
      this.calculateTableScrollHeight();
    }
    this.translateErrorMessages();
    const subscription = this.translationService.onLangChange.subscribe(() => {
      this.translateErrorMessages();
    });

    this.subSink.add(subscription);

    this.subSink.add(
      this.shortPaidStatusService.getShortpaidStatus().subscribe((status) => {
        this.selectedPayOtherShortPaidReason = status;
      }),
    );

    this.subSink.add(
      this.breakpointObserver
        .observe('(min-width: 769px)')
        .subscribe((result) => {
          const freezeColumns =
            result.matches &&
            // primeng is incorrectly rendering the payable
            // restrictions icon column when frozen, so
            // disable freezing when applicable for now
            (this.isReadOnly ||
              this.modifiedSessionDocuments.filter(
                (d) => d.restrictionHints?.length > 0,
              ).length === 0);
          this.columns
            .filter((c) => c.frozen !== undefined)
            .forEach((c) => (c.frozen = freezeColumns));
          this.freezeColumns = freezeColumns;
          this.cdref.detectChanges();
        }),
    );
  }

  ngAfterViewChecked(): void {
    if (
      !this.isRendered &&
      this.shortPayRowIndex >= 0 &&
      this.requiresShortPayReason &&
      !this.selectedPayOtherShortPaidReason
    ) {
      this.isRendered = true;
      const btnId = `penIcon_${this.shortPayRowIndex}`;
      const buttons: HTMLButtonElement[] = Array.from(
        this.el.nativeElement.querySelectorAll('button'),
      );
      const button = buttons.filter((item) => item.id === btnId)[0];
      button.click();
      this.editPaymentReasonDropdownFormControl.markAsTouched();

      const container = document.getElementById('make-payment-root');
      if (container) {
        container.className += 'checkout-overlay';
      }
    }

    this.cdref.detectChanges();
  }

  filterDispute(search: { query: string }): void {
    if (!this.enableAutocompleteDropdownMode) {
      this.filteredDisputeReasonItems = [];
      return;
    }

    const query = search.query ?? '';
    const filtered = this.disputeReasonItems.filter(
      (item) => item.label.toLowerCase().indexOf(query.toLowerCase()) >= 0,
    );

    this.filteredDisputeReasonItems = filtered;
  }

  private loadDisputeReasons() {
    const dropdownList: SelectItem<DisputeReason>[] = [];
    if (this.allowNonDisputeReason) {
      dropdownList.push({
        label: 'Partial Payment',
        value: {
          active: null,
          visibility: 'All',
          createDate: null,
          description: 'Partial Payment',
        },
      });
    }
    this.disputeReasons?.forEach((disputeReason) => {
      const model: SelectItem<DisputeReason> = {
        label: disputeReason.description,
        value: disputeReason,
      };
      dropdownList.push(model);
    });
    this.disputeReasonItems = dropdownList.sort((a, b) => {
      if (a.label < b.label) {
        return -1;
      }
      if (a.label > b.label) {
        return 1;
      }
      return 0;
    });
  }

  get isTableReadOnly(): boolean {
    return this._isReadOnly$.value;
  }

  set isTableReadOnly(isReadonly: boolean) {
    this._isReadOnly$.next(isReadonly);
  }

  get allowShortPay(): boolean {
    return this.paymentRules?.settings?.shortAndOverPaySettings?.allowShortPay;
  }

  get allowOverPay(): boolean {
    return this.paymentRules?.settings?.shortAndOverPaySettings?.allowOverPay;
  }

  get allowNonDisputeReason(): boolean {
    return this.paymentRules?.settings?.shortAndOverPaySettings
      ?.allowNonDisputeReason;
  }

  get allowDisputeReason(): boolean {
    return this.paymentRules?.settings?.shortAndOverPaySettings
      ?.allowDisputeReason;
  }

  get allowFreeFormReason(): boolean {
    return this.paymentRules?.settings?.shortAndOverPaySettings
      ?.allowFreeFormReason;
  }

  get requiresShortPayReason(): boolean {
    return (
      this.allowDisputeReason ||
      this.allowFreeFormReason ||
      this.allowNonDisputeReason
    );
  }

  get isShortPaidRow(): boolean {
    return (
      this.currentRowData.payOtherReasonCode ===
      (PayOtherAmountReasonCodes.DocumentShortPaid as string)
    );
  }

  sortTable(event: SortEvent) {
    sortDocuments(
      event.data as SessionDocumentInfo[],
      event.field,
      event.order,
    );
  }

  onTableSort() {
    this.modifiedSessionDocuments.forEach((value, index) => {
      if (
        value.payOtherReasonCode ===
        (PayOtherAmountReasonCodes.DocumentShortPaid as string)
      ) {
        this.shortPayRowIndex = index;
        return;
      }
    });
  }

  showDialog(
    event: MouseEvent,
    rowData: ModifiedSessionDocumentInfo,
    overlayPanel: OverlayPanel,
  ): void {
    const inputValidators = [Validators.required];
    const currencyInputLabelKey = rowData.isCreditMemo
      ? 'ARC_PAYMENT_CREDIT_MEMO_AMOUNT'
      : 'ARC_PAYMENT_PAYABLE_AMOUNT';

    this.currentRowData = rowData;
    if (rowData.isCreditMemo || rowData.isFullInvoiceDispute) {
      inputValidators.push(
        payableAmountGreaterThanOpenBalanceValidator(
          rowData.isCreditMemo
            ? rowData.originalPayableAmount
            : rowData.openBalance,
        ),
      );
    } else {
      if (this.allowShortPay && rowData.originalPayableAmount > 0) {
        inputValidators.push(Validators.min(0.01));
      } else {
        const discountedAmount = this.getDiscountedAmount(rowData.urn);
        const discountExcludedForNonAmountReasons =
          this.isDiscountLostForNonAmountReasons(rowData.urn);
        inputValidators.push(
          shortPaymentValidator(
            rowData.originalSessionPayableAmount,
            discountedAmount ?? rowData.originalSessionPayableAmount,
            discountExcludedForNonAmountReasons,
          ),
        );
      }

      if (!this.allowOverPay) {
        inputValidators.push(
          overPaymentValidator(rowData.originalSessionPayableAmount),
        );
      }
    }

    this.currencyInputLabel = this.translationService.instant(
      currencyInputLabelKey,
    ) as string;
    this.editableRowData = rowData;
    this.editPaymentCurrencyInput.setValidators(inputValidators);
    this.editPaymentCurrencyInput.setValue(Math.abs(rowData.payableAmount));
    this.currencyOptions = [
      { label: rowData.currencyCode, value: rowData.currencyCode },
    ];
    if (this.canShowDisputeReasonDropdown()) {
      this.filterDispute({ query: rowData.description });
      let value: SelectItem<DisputeReason | string> =
        this.filteredDisputeReasonItems.find(
          (x) => x.label === rowData.description,
        );

      if (value == null && rowData.description != null) {
        value = {
          label: rowData.description,
          value: {
            description: rowData.description,
          },
        };
      }

      this.editPaymentReasonDropdownFormControl.setValue(value);
      this.editPaymentReasonDropdownFormControl.updateValueAndValidity();
    }

    this.editPaymentReasonDropdownFormControl.markAsUntouched();

    // TODO: Revert hide/show workaround once primeng is updated to ^16.0.0.
    // In 15.4.1, toggle throws an error when the overlay is already visible.
    if (overlayPanel.overlayVisible) {
      overlayPanel.hide();
    }

    const showInterval = setInterval(() => {
      if (!overlayPanel.isOverlayAnimationInProgress) {
        clearInterval(showInterval);
        overlayPanel.show(event);
        this.updateReasonDropdownState();
      }
    });
  }

  closeOverlayPanel(): void {
    this.editPaymentOverlayPanel?.hide();
    this.editPaymentReasonDropdownFormControl.removeValidators([
      requiredNoWhitespace(),
    ]);

    this.editPaymentReasonDropdownFormControl.updateValueAndValidity();
  }

  updatePayableAmount(): void {
    this.formGroup.updateValueAndValidity();
    this.formGroup.markAllAsTouched();
    if (this.formGroup.invalid || this.editableRowData == null) {
      return;
    }

    const payableAmount =
      this.editPaymentCurrencyInput.value *
      (this.editableRowData.isCreditMemo ? -1 : 1);

    const editableData = {
      ...this.editableRowData,
      payableAmount,
    };

    if (
      this.canShowDisputeReasonDropdown() &&
      !this.editPaymentReasonDropdownFormControl.disabled
    ) {
      const dropdownValue = this.editPaymentReasonDropdownFormControl
        .value as unknown;
      if (typeof dropdownValue === 'string') {
        editableData.description = dropdownValue;
      } else {
        const selectedItem = dropdownValue as SelectItem<DisputeReason>;
        editableData.disputeReasonId = selectedItem.value.reasonId;
        editableData.description = selectedItem.value.description;
      }
    } else {
      editableData.disputeReasonId = null;
      editableData.description = null;
    }

    // mark row as edited
    this.markDocumentAsModified.emit({
      urn: this.editableRowData.urn,
      source: 'userEdit',
    } as ModifiedDocumentMeta);

    this.editPayableAmount.emit(editableData);
    // emit call to update payment preview
    this.updateAppliedDiscountAmount.emit();

    this.closeOverlayPanel();

    if (this.isShortPaidRow && !this.selectedPayOtherShortPaidReason) {
      document
        .getElementById('make-payment-root')
        ?.classList.remove('checkout-overlay');
      this.shortPaidStatusService.setShortpaidStatus(true);
    }
  }

  private createEditPayableAmountForm(): void {
    this.editPaymentCurrencyInput = this.fb.control(0.0, Validators.required);
    this.currencyInputFormControl = this.fb.group({
      amount: this.editPaymentCurrencyInput,
      currency: null,
    });
    this.editPaymentReasonDropdownFormControl = this.fb.control(null);
    this.formGroup = this.fb.group({
      currencyInput: this.currencyInputFormControl,
      editPaymentReasonDropdown: this.editPaymentReasonDropdownFormControl,
    });

    this.subSink.add(
      this.editPaymentCurrencyInput.valueChanges.subscribe(() => {
        this.onPayableAmountChanged();
      }),
    );
  }

  canShowDisputeReasonDropdown(): boolean {
    if (
      this.editableRowData == null ||
      this.editableRowData.isCreditMemo ||
      this.editableRowData.isFullInvoiceDispute
    ) {
      return false;
    }

    const isOverPay = this.isCurrentPayableAmountOverPay();

    if (isOverPay && !this.allowFreeFormReason) {
      return false;
    }

    return (
      (this.allowShortPay || this.allowOverPay) &&
      (this.allowFreeFormReason || this.disputeReasonItems?.length > 0)
    );
  }

  onSelectionChanged(selection: unknown) {
    if (this.isReadOnly) {
      return;
    }

    this.selectedDocumentsChanged.emit(
      selection as ModifiedSessionDocumentInfo[],
    );
  }

  private onPayableAmountChanged() {
    this.updateReasonDropdownState();
    this.updateReasonDropdownMode();
  }

  private updateReasonDropdownMode() {
    const isOverPay = this.isCurrentPayableAmountOverPay();

    this.enableAutocompleteDropdownMode =
      this.disputeReasonItems.length > 0 && !isOverPay;
    if (!this.enableAutocompleteDropdownMode) {
      this.reasonDropdownPlaceHolderKey = 'ARC_PAYMENT_SHORT_PAY_ENTER_REASON';
    } else {
      this.reasonDropdownPlaceHolderKey = this.allowFreeFormReason
        ? 'ARC_PAYMENT_SHORT_PAY_SELECT_OR_ENTER_REASON'
        : 'ARC_PAYMENT_SHORT_PAY_SELECT_REASON';
    }
  }

  private updateReasonDropdownState() {
    const shouldClearReasonEntry = this.shouldClearReasonEntry();
    const isOverPay = this.isCurrentPayableAmountOverPay();
    const isShortPay = this.isCurrentPayableAmountShortPay();

    const hasSelectedReason =
      typeof (
        this.editPaymentReasonDropdownFormControl
          .value as SelectItem<DisputeReason>
      )?.value?.visibility === 'string';
    const canClearReason =
      shouldClearReasonEntry ||
      (this.allowOverPay &&
        isOverPay &&
        (!this.allowFreeFormReason || hasSelectedReason));

    if (canClearReason) {
      this.editPaymentReasonDropdownFormControl.setValue(null);
    }

    const isOverPayReasonRequired = isOverPay && this.allowFreeFormReason;
    const isShortPayReasonRequired =
      isShortPay &&
      (this.disputeReasonItems.length > 0 || this.allowFreeFormReason) &&
      !this.skipReasonEntryForDiscountedPayable();

    const canEnable =
      this.editPaymentReasonDropdownFormControl.value != null ||
      (!this.editableRowData.isCreditMemo &&
        !this.editableRowData.isFullInvoiceDispute &&
        this.editPaymentCurrencyInput.valid &&
        (isOverPayReasonRequired || isShortPayReasonRequired));

    let validators = [];
    if (canEnable) {
      validators = [requiredNoWhitespace()];
      this.editPaymentReasonDropdownFormControl.enable();
    } else {
      this.editPaymentReasonDropdownFormControl.disable();
    }

    this.editPaymentReasonDropdownFormControl.setValidators(validators);
    this.editPaymentReasonDropdownFormControl.updateValueAndValidity();
  }

  private onDocumentsChanged(value: ModifiedSessionDocumentInfo[]) {
    this.modifiedSessionDocuments = value || [];
    this.selectedDocuments = this.modifiedSessionDocuments.filter(
      (x) => x.isSelected,
    );

    const {
      payableRestrictions,
      confirmationNumber,
      transactionNumber,
      buyerAccount,
      poNumber,
      openBalance,
      originalBalance,
      documentDate,
      dueDate,
      discountDate,
      amountPaid,
      discountAmount,
      editPayableAmount,
      disputedAmount,
    } = this.columnMap;

    payableRestrictions.visible = false;

    for (const document of this.modifiedSessionDocuments) {
      document.isPayableAmountEditable = this.isDocumentEditable(document);

      if (!editPayableAmount.visible) {
        editPayableAmount.visible = document.isPayableAmountEditable;
      }

      if (!payableRestrictions.visible) {
        payableRestrictions.visible = document.restrictionHints?.length > 0;
      }

      if (!poNumber.visible) {
        poNumber.visible = !!document.poNumber;
      }

      if (!openBalance.visible) {
        openBalance.visible = document.openBalance !== null;
      }

      if (!originalBalance.visible) {
        originalBalance.visible = document.originalBalance !== null;
      }

      if (!documentDate.visible) {
        documentDate.visible = !!document.documentDate;
      }

      if (!discountDate.visible) {
        discountDate.visible =
          document.isPayable &&
          !!document.discountState &&
          (!document.isCreditMemo || document.appliedDiscountAmount != 0);
        discountAmount.visible = discountDate.visible;
      }
      if (!dueDate.visible) {
        dueDate.visible = !!document.dueDate;
      }

      if (!amountPaid.visible) {
        amountPaid.visible = document.amountPaid !== null;
      }

      if (!confirmationNumber.visible) {
        confirmationNumber.visible =
          !!document.confirmationNumber && this.hasMultipleTransactions;
      }

      if (!transactionNumber.visible) {
        transactionNumber.visible =
          !!document.transactionNumber && this.hasMultipleTransactions;
      }

      if (!disputedAmount.visible) {
        disputedAmount.visible =
          !!document.disputeTotal && document.disputeTotal != 0;
      }
    }

    buyerAccount.visible =
      this.modifiedSessionDocuments
        .map((x) => x.buyerAccount)
        .filter((account, a, accounts) => accounts.indexOf(account) === a)
        .length > 1;

    this.excludedColumns?.forEach((column) => {
      this.columnMap[column].visible = false;
    });

    this.preserveScrollPosition();
    this.fixFrozenColumnLeftPosition();
  }

  private preserveScrollPosition() {
    const table = this.documentTable?.wrapperViewChild
      ?.nativeElement as HTMLElement;
    const left = table?.scrollLeft;
    const top = table?.scrollTop;
    setTimeout(() => table?.scrollTo(left, top));
  }

  private fixFrozenColumnLeftPosition() {
    window.clearTimeout(this.resizeTimer);
    // trigger FrozenColumn.recalculateColumns to fix left position
    this.resizeTimer = window.setTimeout(() =>
      window.dispatchEvent(new Event('resize')),
    );
  }

  private calculateTableScrollHeight() {
    const minPageHeight = 780;
    const minScrollHeight = 175;
    const singleRowHeight = 55;

    this.windowHeight = window.innerHeight;
    if (this.windowHeight > minPageHeight) {
      const difference =
        Number(this.windowHeight - minPageHeight) > singleRowHeight
          ? (this.windowHeight - minPageHeight) / singleRowHeight
          : 0;
      const extendedScrollHeight = singleRowHeight * difference;
      this.scrollHeight = `${(
        minScrollHeight + extendedScrollHeight
      ).toString()}px`;
    }
    this.cdref.detectChanges();
  }

  private isDocumentEditable(document: ModifiedSessionDocumentInfo): boolean {
    if (this.isReadOnly) return false;

    const {
      isPayable,
      isCreditMemo,
      isFullInvoiceDispute,
      originalPayableAmount,
      documentType,
    } = document;

    if (!isPayable) return false;

    // includes negative statements
    if (isCreditMemo) return originalPayableAmount !== 0;

    if (isFullInvoiceDispute) return true;

    // after this point, short and/or over pay must be allowed to edit
    if (!this.allowShortPay && !this.allowOverPay) return false;

    // nonzero invoices are editable. until we respect the pay on
    // statement rule, nonzero statements are editable, too.
    if (originalPayableAmount !== 0) return true;

    // zero-amount invoices can be overpaid. zero-amount statements cannot be overpaid
    // until we respect the pay on statement rule because eIC sends zero to enforce it.
    if (
      this.allowOverPay &&
      (documentType !== 'statement' || this.statementPaymentAllowed)
    ) {
      return true;
    }

    return false;
  }

  private initializeColumns() {
    this.columnMap = {
      payableRestrictions: {
        field: 'payableRestrictions',
        header: '',
        visible: false,
        sortable: false,
        wrap: false,
        frozen: true,
        width: '36px',
      },
      invoiceNumber: {
        field: 'invoiceNumber',
        header: 'ARC_PAYMENT_DOCUMENT',
        visible: true,
        sortable: false,
        wrap: true,
        frozen: true,
      },
      confirmationNumber: {
        field: 'confirmationNumber',
        header: 'ARC_PAYMENT_CONFIRMATION_NUMBER',
        visible: false,
        sortable: true,
        wrap: true,
      },
      transactionNumber: {
        field: 'transactionNumber',
        header: 'ARC_PAYMENT_TRANSACTION_NUMBER',
        visible: false,
        sortable: true,
        wrap: true,
      },
      buyerAccount: {
        field: 'buyerAccount',
        header: 'ARC_PAYMENT_ACCOUNT',
        visible: false,
        sortable: false,
        wrap: true,
      },
      poNumber: {
        field: 'poNumber',
        header: 'ARC_PAYMENT_PO_NUMBER',
        visible: false,
        sortable: false,
        wrap: true,
      },
      documentDate: {
        field: 'documentDate',
        header: 'ARC_PAYMENT_DOCUMENT_DATE',
        visible: false,
        sortable: true,
      },
      dueDate: {
        field: 'dueDate',
        header: 'ARC_PAYMENT_DUE_DATE',
        visible: false,
        sortable: true,
      },
      discountDate: {
        field: 'discountDate',
        header: 'ARC_PAYMENT_DISCOUNT_DATE',
        visible: false,
        sortable: true,
      },
      discountAmount: {
        field: 'discountAmount',
        header: 'ARC_PAYMENT_DISCOUNT_AMOUNT',
        visible: false,
        sortable: true,
        currency: true,
        width: '140px',
      },
      originalBalance: {
        field: 'originalBalance',
        header: 'ARC_PAYMENT_LABEL_ORIGINAL_BALANCE',
        visible: false,
        sortable: false,
        currency: true,
      },
      openBalance: {
        field: 'openBalance',
        header: 'ARC_PAYMENT_LABEL_OPEN_BALANCE',
        visible: false,
        sortable: false,
        currency: true,
      },
      disputedAmount: {
        field: 'disputedAmount',
        header: 'ARC_PAYMENT_LABEL_DISPUTED_AMOUNT',
        visible: false,
        sortable: false,
        currency: true,
      },
      amountPaid: {
        field: 'amountPaid',
        header: 'ARC_PAYMENT_LABEL_AMOUNT_PAID',
        visible: false,
        sortable: false,
        currency: true,
      },
      payableAmount: {
        field: 'payableAmount',
        header: 'ARC_PAYMENT_PAYABLE_AMOUNT',
        visible: true,
        sortable: false,
        currency: true,
        frozen: true,
        alignFrozen: 'right',
      },
      editPayableAmount: {
        field: 'editPayableAmount',
        header: '',
        visible: false,
        sortable: false,
        frozen: true,
        alignFrozen: 'right',
        width: '36px',
      },
    };

    this.columns = Object.values(this.columnMap);
  }

  private translateErrorMessages() {
    this.errorMessages = {
      isValueGreaterThanOpenBalance: this.translationService.instant(
        'ARC_PAYMENT_EDIT_PAYABLE_EXCEED_OPEN_BALANCE_ERROR',
      ) as string,

      isOverPayment: this.translationService.instant(
        'ARC_PAYMENT_EDIT_PAYABLE_OVER_PAYMENT_ERROR',
      ) as string,

      isShortPayment: this.translationService.instant(
        'ARC_PAYMENT_EDIT_PAYABLE_SHORT_PAYMENT_ERROR',
      ) as string,
    };
  }

  translateTooltip(tokens: string[]): string {
    return tokens
      .map((t) => this.translationService.instant(t) as string)
      .join('\n\n');
  }

  /**
   * Row glyph for restrictionHints.
   * 48h HasPastPayment → schedule (clock) per UX mock; hard block → warning;
   * other payable hints (e.g. soft paid-in-full) → info.
   */
  restrictionIcon(rowData: {
    isPayable?: boolean;
    isPaid?: string;
    restrictionHints?: string[];
  }): string {
    const hints = rowData.restrictionHints ?? [];
    const isRecent48h =
      rowData.isPaid === PayablePaymentStatus.HasPastPayment ||
      hints.includes('ARC_PAYMENT_DUPLICATE_PAYMENT_TOOLTIP_WARNING_RECENT');
    if (isRecent48h) {
      return 'schedule';
    }
    return rowData.isPayable ? 'info' : 'warning';
  }

  private shouldClearReasonEntry() {
    const discountedAmount = this.getDiscountedAmount(
      this.editableRowData?.urn,
    );
    return discountedAmount
      ? this.editPaymentCurrencyInput?.value ==
          Math.abs(this.editableRowData?.originalPayableAmount) ||
          this.skipReasonEntryForDiscountedPayable()
      : this.editPaymentCurrencyInput?.value ==
          Math.abs(this.editableRowData?.originalPayableAmount);
  }

  private isCurrentPayableAmountOverPay() {
    return (
      !this.editableRowData?.isFullInvoiceDispute &&
      this.editPaymentCurrencyInput?.value >
        Math.abs(this.editableRowData?.originalSessionPayableAmount)
    );
  }

  private isCurrentPayableAmountShortPay() {
    return (
      this.editPaymentCurrencyInput?.value <
      Math.abs(this.editableRowData?.originalSessionPayableAmount)
    );
  }

  private skipReasonEntryForDiscountedPayable() {
    const discountedAmount = this.getDiscountedAmount(
      this.editableRowData?.urn,
    );

    if (discountedAmount == null) {
      return false;
    }

    const reasonNotRequired =
      this.editPaymentCurrencyInput.value >= discountedAmount &&
      this.editPaymentCurrencyInput.value <=
        this.editableRowData.originalSessionPayableAmount &&
      !this.isDiscountLostForNonAmountReasons(this.editableRowData?.urn);

    const hasDiscount = this.editableRowData.discountAmount > 0;

    return hasDiscount && reasonNotRequired;
  }

  private getDiscountedAmount(urn: string): number {
    const discountMeta = this.discountMetadata.find((d) => d.urn === urn);
    return discountMeta?.discountedPayableAmount;
  }

  private isDiscountLostForNonAmountReasons(urn: string): boolean {
    const payable = this.previewPayables.find((p) => p.burn === urn);
    if (!payable?.discountExclusionReasons) {
      return true;
    }

    const nonAmountReasons = [
      DiscountExclusionReason.DiscountExpired.toString(),
      DiscountExclusionReason.ScheduledPaymentDateIsAfterDiscountExpiration.toString(),
      DiscountExclusionReason.PaymentMethodNotDiscountable.toString(),
      DiscountExclusionReason.NotEnoughDiscountInfo.toString(),
    ];

    return payable.discountExclusionReasons.some((reason) =>
      nonAmountReasons.includes(reason),
    );
  }

  ngOnDestroy(): void {
    this.subSink.unsubscribe();
    this._isReadOnly$.unsubscribe();
  }
}
